import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUploadSection = ({ documents, onUpload, onDelete }) => {
  const [dragActive, setDragActive] = useState(false);

  const documentTypes = [
    { id: 'resume', label: 'Resume/CV', icon: 'FileText', accept: '.pdf,.doc,.docx' },
    { id: 'marksheet', label: 'Academic Marksheets', icon: 'FileSpreadsheet', accept: '.pdf,.jpg,.jpeg,.png' },
    { id: 'certificate', label: 'Certificates', icon: 'Award', accept: '.pdf,.jpg,.jpeg,.png' },
    { id: 'id-proof', label: 'ID Proof', icon: 'CreditCard', accept: '.pdf,.jpg,.jpeg,.png' }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e, type) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileUpload(e?.dataTransfer?.files?.[0], type);
    }
  };

  const handleFileUpload = (file, type) => {
    const mockDocument = {
      id: Date.now(),
      type,
      name: file?.name,
      size: file?.size,
      uploadDate: new Date()?.toISOString(),
      status: 'uploaded'
    };
    onUpload(mockDocument);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg md:text-xl font-heading font-semibold text-card-foreground mb-4">
        Document Upload
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {documentTypes?.map((docType) => {
          const uploadedDocs = documents?.filter((d) => d?.type === docType?.id);

          return (
            <div key={docType?.id} className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={docType?.icon} size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-card-foreground">{docType?.label}</h4>
                  <p className="text-xs text-muted-foreground">
                    {uploadedDocs?.length} file{uploadedDocs?.length !== 1 ? 's' : ''} uploaded
                  </p>
                </div>
              </div>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-smooth ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={(e) => handleDrop(e, docType?.id)}
              >
                <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-card-foreground mb-2">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Supported: {docType?.accept?.replace(/\./g, '')?.toUpperCase()}
                </p>
                <input
                  type="file"
                  accept={docType?.accept}
                  onChange={(e) => e?.target?.files?.[0] && handleFileUpload(e?.target?.files?.[0], docType?.id)}
                  className="hidden"
                  id={`file-${docType?.id}`}
                />
                <label htmlFor={`file-${docType?.id}`}>
                  <Button variant="outline" size="sm" asChild>
                    <span>Choose File</span>
                  </Button>
                </label>
              </div>
              {uploadedDocs?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedDocs?.map((doc) => (
                    <div
                      key={doc?.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Icon name="File" size={16} color="var(--color-muted-foreground)" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-card-foreground truncate">
                            {doc?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(doc?.size)} • {new Date(doc.uploadDate)?.toLocaleDateString('en-IN')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => onDelete(doc?.id)}
                        className="text-error hover:text-error/80 transition-smooth ml-2"
                        aria-label="Delete document"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
        <div className="text-sm text-card-foreground">
          <p className="font-medium mb-1">Document Guidelines:</p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Maximum file size: 5MB per document</li>
            <li>Ensure documents are clear and readable</li>
            <li>All certificates must be verified copies</li>
            <li>Resume should be updated with latest information</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadSection;